import { inject } from '@angular/core';
import { Router, Routes } from '@angular/router';
import { HomeComponent } from './home/home';
import { authGuard } from '@shared/guards/auth.guard';
import { clubGuestGuard } from './guards/club-guest.guard';
import { maintenanceGuard } from '@shared/guards/maintenance.guard';
import { MaintenanceComponent } from './maintenance/maintenance';

export const routes: Routes = [
  { path: 'maintenance', component: MaintenanceComponent, canActivate: [maintenanceGuard] },
  {
    path: '',
    canActivate: [maintenanceGuard],
    children: [
      { path: '', canActivate: [clubGuestGuard], loadComponent: () => import('./auth/login/login').then(m => m.LoginComponent) },
      { path: 'events', component: HomeComponent },
      {
        path: 'auth',
        children: [
          // `/auth/login` is just an alias for the root login page. A *static*
          // redirectTo drops query params, which silently breaks the
          // authGuard's `?returnUrl=…` (e.g. logged-out /shop → login → /shop).
          // A functional redirect forwards the query params to the root route,
          // where LoginComponent reads returnUrl off window.location.search.
          { path: 'login', redirectTo: ({ queryParams }) => inject(Router).createUrlTree(['/'], { queryParams }) },
          { path: 'register', canActivate: [clubGuestGuard], loadComponent: () => import('./auth/register/register').then(m => m.RegisterComponent) },
          { path: 'register-success', loadComponent: () => import('./auth/register-success/register-success').then(m => m.RegisterSuccessComponent) },
          { path: 'forgot-password', canActivate: [clubGuestGuard], loadComponent: () => import('./auth/forgot-password/forgot-password').then(m => m.ForgotPasswordComponent) },
          { path: 'reset-password', canActivate: [clubGuestGuard], loadComponent: () => import('./auth/reset-password/reset-password').then(m => m.ResetPasswordComponent) },
          { path: 'change-password', canActivate: [authGuard], loadComponent: () => import('./auth/change-password/change-password').then(m => m.ChangePasswordComponent) },
          { path: 'verify-email-notice', canActivate: [authGuard], loadComponent: () => import('./auth/verify-email-notice/verify-email-notice').then(m => m.VerifyEmailNoticeComponent) },
        ]
      },
      { path: 'verify-email', loadComponent: () => import('./auth/verify-email/verify-email').then(m => m.VerifyEmailComponent) },
      { path: 'verify-waiver', loadComponent: () => import('./events/verify-waiver/verify-waiver').then(m => m.VerifyWaiverComponent) },
      { path: 'server-error', loadComponent: () => import('./error/server-error/server-error').then(m => m.ServerErrorComponent) },
      // No guard: the component ingests the impersonation token from the URL and
      // bootstraps the session before routing on.
      { path: 'impersonate', loadComponent: () => import('./impersonate/impersonate').then(m => m.ImpersonateComponent) },
      {
        path: 'dashboard',
        canActivate: [authGuard],
        children: [
          { path: '', loadComponent: () => import('./dashboard/dashboard').then(m => m.DashboardComponent) },
          { path: 'edit-profile', loadComponent: () => import('./dashboard/edit-profile/edit-profile').then(m => m.EditProfileComponent) },
          { path: 'events/:id', loadComponent: () => import('./dashboard/event-detail/event-detail').then(m => m.EventDetailComponent) },
        ]
      },
      {
        path: 'events/:id/register',
        canActivate: [authGuard],
        loadComponent: () => import('./events/registration/registration').then(m => m.RegistrationComponent)
      },
      {
        // Issue #620 (Phase 5 / T5.3): standalone post-payment buy page.
        path: 'events/:id/experiences/buy',
        canActivate: [authGuard],
        loadComponent: () => import('./events/experiences-buy/experiences-buy').then(m => m.ExperiencesBuyComponent)
      },
      {
        path: 'events/payment/:orderId',
        canActivate: [authGuard],
        loadComponent: () => import('./events/payment-step/payment-step').then(m => m.PaymentStepComponent)
      },
      {
        path: 'events/payment/:orderId/success',
        canActivate: [authGuard],
        loadComponent: () => import('./events/payment-success/payment-success').then(m => m.PaymentSuccessComponent)
      },
      {
        // Issue #581 / Sentry CLUB-2: the navbar's EVENIMENTE dropdown and
        // externally shared links point at /events/:id, which never had a
        // component — the router threw an uncaught NG04002 and the click died
        // silently. Funnel it into the registration page; authGuard adds the
        // login round-trip (with returnUrl) for logged-out visitors. Keep this
        // AFTER the more specific events/* routes above.
        path: 'events/:id',
        redirectTo: 'events/:id/register',
      },
      {
        path: 'shop',
        canActivate: [authGuard],
        children: [
          { path: '', loadComponent: () => import('./shop/shop-landing/shop-landing').then(m => m.ShopLandingComponent) },
          { path: 'events/:eventId', loadComponent: () => import('./shop/shop-browse/shop-browse').then(m => m.ShopBrowseComponent) },
          { path: 'events/:eventId/cart', loadComponent: () => import('./shop/cart-checkout/cart-checkout').then(m => m.CartCheckoutComponent) },
          { path: 'events/:eventId/checkout-pending', loadComponent: () => import('./shop/checkout-pending/checkout-pending').then(m => m.CheckoutPendingComponent) },
        ]
      },
      {
        path: 'orders',
        canActivate: [authGuard],
        children: [
          { path: '', loadComponent: () => import('./orders/orders-list/orders-list').then(m => m.OrdersListComponent) },
          { path: ':uuid', loadComponent: () => import('./orders/order-detail/order-detail').then(m => m.OrderDetailComponent) },
        ]
      },
      {
        path: 'checkin/:id',
        canActivate: [authGuard],
        loadComponent: () => import('./checkin/checkin').then(m => m.CheckinComponent)
      },
      {
        // Auth-decision handled inside the component (unauth path stashes a
        // pending claim in sessionStorage and redirects to login). No guard so
        // the component can run that flow.
        path: 'group/claim/:token',
        loadComponent: () => import('./group/claim/claim').then(m => m.GroupClaimComponent)
      },
      {
        path: 'group/request-access',
        loadComponent: () => import('./group/request-access/request-access').then(m => m.GroupRequestAccessComponent)
      },
      {
        // No guard: the component resolves the viewer from the SPA token via
        // scan-resolve (a QR browser navigation can't carry it server-side).
        path: 'scan/:hash',
        loadComponent: () => import('./scan/scan').then(m => m.ClubScanComponent)
      },
      {
        // Safety net (#581): any other unmatched deep link (truncated shares,
        // typos) lands on the public events home instead of an uncaught
        // NG04002. Must stay LAST — '**' matches everything.
        path: '**',
        redirectTo: 'events',
      },
    ]
  }
];
